import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import Label from '../../../../components/Label';
import useSettings from '../../../../hooks/useSettings';
import { get } from '../../../../inteceptor';
import { PATH_DASHBOARD } from '../../../../routes/paths';

const USER_COLUMNS = [
  {
    accessorKey: 'id',
    header: '#id',
    size: 80,
  },
  {
    accessorKey: 'name',
    header: 'Name',
    size: 220,
  },
  {
    accessorKey: 'email',
    header: 'Email',
    size: 260,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    Cell: ({ cell }) => (
      <Label color={cell.getValue() === 'active' ? 'success' : cell.getValue() === 'deleted' ? 'error' : 'warning'} sx={{ textTransform: 'capitalize' }}>
        {cell.getValue()}
      </Label>
    ),
  },
  {
    accessorKey: 'isSuperAdmin',
    header: 'Super Admin',
    size: 130,
    Cell: ({ cell }) =>
      cell.getValue() ? (
        <CheckCircleIcon color='success' fontSize='small' />
      ) : (
        <RemoveIcon color='disabled' fontSize='small' />
      ),
  },
  {
    accessorKey: 'locationId',
    header: 'Location',
    size: 110,
  },
  {
    accessorKey: 'userProfileID',
    header: 'Profile',
    size: 110,
  },
];

const buildPermissionColumns = (permissions) =>
  permissions.map((permission) => ({
    accessorKey: permission.code,
    header: permission.title,
    size: Math.min(Math.max((permission.title?.length || permission.code.length) * 8 + 32, 120), 260),
    Header: () => (
      <Tooltip
        arrow
        title={
          <Box>
            <Typography variant='caption' display='block'>
              {permission.code}
            </Typography>
            <Typography variant='caption' display='block'>
              {permission.mainCategory} / {permission.category}
            </Typography>
          </Box>
        }
      >
        <Box component='span' sx={{ display: 'block', lineHeight: 1.25, overflow: 'visible', whiteSpace: 'normal', wordBreak: 'normal' }}>
          {permission.title}
        </Box>
      </Tooltip>
    ),
    Cell: ({ cell }) =>
      cell.getValue() ? (
        <CheckCircleIcon color='success' fontSize='small' />
      ) : (
        <RemoveIcon color='disabled' fontSize='small' />
      ),
  }));

export default function UserPermissionsReport() {
  const { themeStretch } = useSettings();
  const [rows, setRows] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      setIsLoading(true);
      try {
        const response = await get('userPermissionsReport');
        if (response.status) {
          setRows(response.data?.rows || []);
          setPermissions(response.data?.permissions || []);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, []);

  const columns = useMemo(() => USER_COLUMNS.concat(buildPermissionColumns(permissions)), [permissions]);

  return (
    <Page title='User Permissions Report'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='User Permissions Report'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Reports', href: PATH_DASHBOARD.reports.root }, { name: 'HR Reports' }, { name: 'User Permissions' }]}
        />

        <DataGridTable
          name='User Permissions Report'
          data={rows}
          column={columns}
          isLoading={isLoading}
          enableRowActions={false}
          enableRowSelection={false}
          isRowClickable={false}
          rowsPerPage={50}
          maxHeight='70vh'
          pinnedLeftColumns={['id', 'name', 'email', 'status', 'isSuperAdmin']}
        />
      </Container>
    </Page>
  );
}
